import { useQuery } from "react-query";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import usePaginationFromEndpoint, {
    usePaginationFromEndpointProps,
} from "@/components/templates/PaginatedList/usePaginationFromEndpoint";
import useSearchFromEndpoint, {
    useSearchFromEndpointProps,
} from "@/components/templates/SearchList/useSearchFromEndpoint";
import { FieldPath } from "@/core/fieldPath";
import { insuredGoodApi } from "@/shared/apis";
import { InsuredGoodProfessionalQueryParameters, InsuredGoodQueryParameters } from "@/shared/apis/insuredGoodApi";
import { InsuredGood, InsuredGoodPagination, InsuredGoodSortProperty, SortOrder } from "@/shared/domain";
import { useGlobalFilter, usePolicyholders } from "@/shared/hooks";

type useInsuredGoodsQueriesProps = {
    tableMode: boolean;
    useInsuredGoodsPageQuery: ({
        defaultCategory,
    }) => usePaginationFromEndpointProps<InsuredGood, InsuredGoodSortProperty>;
    useInsuredGoodsCategoriesQuery: ({ enabled }) => useInsuredGoodsCategoriesQueryProps;
    useInsuredGoodsSearchQuery: ({ textSearch, sortProperty }) => useSearchFromEndpointProps<InsuredGood>;
};

type useInsuredGoodsCategoriesQueryProps = {
    error: unknown;
    insuredGoodCategories: MyBrokerApi.CategoryCounters[];
    isLoadingCategories: boolean;
    isFetchedCategories: boolean;
};

export const getInsuredGoodSearchFilters = (withRelatedContracts = false): FieldPath<InsuredGood>[] => {
    const insuredGoodSearchFilters: FieldPath<InsuredGood>[] = ["name", "policyHolder", "type.name"];
    insuredGoodSearchFilters.pushIf(withRelatedContracts, "relatedContracts.reference");
    insuredGoodSearchFilters.pushIf(withRelatedContracts, "relatedContracts.branch.name");

    return insuredGoodSearchFilters;
};

const useInsuredGoodsQueries = (): useInsuredGoodsQueriesProps => {
    const { isProEnvironment } = useGlobalFilter();
    const { policyholders } = usePolicyholders({
        checkRight: (a) => a.canViewRisks,
    });
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const tableMode = isProEnvironment && !isSmallScreen;

    const insuredGoodEndpoint = isProEnvironment
        ? insuredGoodApi.getInsuredGoodsForProfessional
        : insuredGoodApi.getInsuredGoods;

    const useInsuredGoodsPageQuery = ({ defaultCategory }) => {
        return usePaginationFromEndpoint<
            InsuredGood,
            InsuredGoodPagination,
            InsuredGoodQueryParameters,
            InsuredGoodSortProperty
        >({
            objectType: "insuredgood",
            tableMode,
            policyholdersIdList: policyholders,
            endPoint: insuredGoodEndpoint,
            defaultCategory,
            defaultSorting: { property: "type.id", order: SortOrder.Ascending },
        });
    };

    const useInsuredGoodsCategoriesQuery = ({
        enabled = true,
    }: {
        enabled: boolean;
    }): useInsuredGoodsCategoriesQueryProps => {
        const {
            data: insuredGoodCategories = [],
            isFetched: isFetchedCategories,
            isLoading: isLoadingCategories,
            error,
        } = useQuery(
            ["insuredGoodCategories", { policyholders }],
            () => insuredGoodApi.getInsuredGoodCategories(policyholders),
            { enabled },
        );

        return {
            error,
            insuredGoodCategories,
            isLoadingCategories,
            isFetchedCategories,
        };
    };

    const useInsuredGoodsSearchQuery = ({ textSearch, sortProperty }) => {
        const filters = getInsuredGoodSearchFilters(tableMode);

        const filterInsuredGoods = (results: InsuredGood[]) =>
            results?.filterBy(textSearch, filters).sortBy(sortProperty) || [];

        return useSearchFromEndpoint<
            InsuredGood,
            InsuredGoodPagination,
            InsuredGoodQueryParameters | InsuredGoodProfessionalQueryParameters
        >("insuredGoods", insuredGoodEndpoint, policyholders, filterInsuredGoods);
    };

    return {
        tableMode,
        useInsuredGoodsPageQuery,
        useInsuredGoodsCategoriesQuery,
        useInsuredGoodsSearchQuery,
    };
};

export default useInsuredGoodsQueries;
